import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Loader2, RefreshCw, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminListAllPrayers, deletePrayer } from '../lib/api';
import { useConfirmDialog } from '../components/ConfirmDialog';

/**
 * Admin Prayers listing (`/admin/prayers`). Reads every non-deleted
 * prayer via `GET /api/admin/prayers`, renders a sortable table, and
 * lets admins delete any row via the shared `DELETE /api/prayers/{id}`
 * endpoint (backend already grants admins delete permission for any
 * prayer with a `deleted_by: admin` audit stamp).
 */
const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const COLUMNS = [
  { key: 'owner',       label: 'Owner' },
  { key: 'title',       label: 'Title' },
  { key: 'created_at',  label: 'Created' },
  { key: 'updated_at',  label: 'Updated' },
  { key: 'perspective', label: 'Voice' },
  { key: 'type',        label: 'Type' },
  { key: 'location',    label: 'Location' },
  { key: 'plays_count', label: 'Watched Count' },
];

// Compact colour + label pair for the "Type" pill. Kept alongside the
// column definition so a future addition (e.g. "poem") only needs one
// edit to appear in both the sort switch and the render path.
const TYPE_META = {
  video:      { label: 'Video',      cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  voice:      { label: 'Voice',      cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
  assistance: { label: 'Assistance', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  text:       { label: 'Text',       cls: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300' },
};

const AdminPrayers = () => {
  const { user, loading: authLoading } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [removingId, setRemovingId] = useState(null);
  const [banner, setBanner] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await adminListAllPrayers();
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load prayers');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const compareStr = (a, b) => (a || '').toString().toLowerCase().localeCompare((b || '').toString().toLowerCase());
    const key = sortKey;
    const list = [...rows];
    list.sort((a, b) => {
      switch (key) {
        case 'owner':      return dir * compareStr(a.user_handle || a.user_name || '', b.user_handle || b.user_name || '');
        case 'title':      return dir * compareStr(a.title, b.title);
        case 'created_at': return dir * compareStr(a.created_at, b.created_at);
        case 'updated_at': return dir * compareStr(a.updated_at, b.updated_at);
        case 'perspective':return dir * compareStr(a.perspective_type, b.perspective_type);
        case 'type':       return dir * compareStr(a.type || 'text', b.type || 'text');
        case 'location': {
          // Rows without coords sort to the bottom regardless of dir so
          // the reviewer's eyes always land on populated cells first.
          const aHas = a.creator_lat != null && a.creator_lon != null;
          const bHas = b.creator_lat != null && b.creator_lon != null;
          if (aHas !== bHas) return aHas ? -1 : 1;
          if (!aHas) return 0;
          // Prefer human-readable area label; fall back to lat string
          // so rows still get a stable order while geocoding resolves.
          const aKey = a.location_area || `${a.creator_lat}`;
          const bKey = b.location_area || `${b.creator_lat}`;
          return dir * compareStr(aKey, bKey);
        }
        case 'plays_count':return dir * ((Number(a.plays_count) || 0) - (Number(b.plays_count) || 0));
        default: return 0;
      }
    });
    return list;
  }, [rows, sortKey, sortDir]);

  const setSort = (key) => {
    if (key === sortKey) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }
    else { setSortKey(key); setSortDir('desc'); }
  };

  const handleDelete = async (row) => {
    const label = row.title || row.id;
    const confirmed = await confirm({
      title: `Admin delete this prayer?`,
      description: `Remove "${label}" by @${row.user_handle || row.user_name || 'user'}? This will hide it from every public feed. This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!confirmed) return;
    setRemovingId(row.id);
    setBanner(null);
    try {
      await deletePrayer(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setBanner({ kind: 'ok', text: `Removed "${label}".` });
    } catch (e) {
      setBanner({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Delete failed' });
    } finally {
      setRemovingId(null);
    }
  };

  if (authLoading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /> Loading…</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <ClipboardList size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-prayers-title">Admin · Prayers</h1>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-500" data-testid="admin-prayers-count">{sortedRows.length} of {rows.length}</span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="refresh-admin-prayers-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      {err && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="admin-prayers-error">{err}</div>}
      {banner && (
        <div
          data-testid="admin-prayers-banner"
          className={`p-3 rounded-lg text-sm mb-4 ${banner.kind === 'ok' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}
        >
          {banner.text}
        </div>
      )}

      <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full text-sm min-w-[720px]" data-testid="admin-prayers-table">
          <thead className="bg-zinc-50 dark:bg-zinc-900/60 text-left">
            <tr className="text-xs uppercase tracking-wide text-zinc-500">
              {COLUMNS.map((c) => {
                const isActive = sortKey === c.key;
                return (
                  <th
                    key={c.key}
                    className="px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap"
                    data-testid={`sort-col-${c.key}`}
                    onClick={() => setSort(c.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {isActive && (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                    </span>
                  </th>
                );
              })}
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-4 py-10 text-center text-zinc-500">
                  <Loader2 className="animate-spin inline mr-2" size={14} /> Loading prayers…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-4 py-10 text-center text-zinc-500" data-testid="admin-prayers-empty">
                  No prayers.
                </td>
              </tr>
            ) : (
              sortedRows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`admin-prayer-row-${r.id}`}
                  className="hover:bg-zinc-50 dark:hover:bg-zinc-900/40 align-top"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {/* Avatar — real image when available, otherwise
                          an initial-based amber fallback. Kept at 28px
                          so it aligns cleanly with the surrounding
                          text-sm handle + text-xs name pairing. */}
                      {r.user_picture ? (
                        <img
                          src={r.user_picture}
                          alt=""
                          data-testid={`admin-prayer-owner-avatar-${r.id}`}
                          className="w-7 h-7 rounded-full object-cover bg-zinc-200 dark:bg-zinc-700 flex-shrink-0"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div
                          data-testid={`admin-prayer-owner-avatar-fallback-${r.id}`}
                          className="w-7 h-7 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-xs font-semibold flex items-center justify-center flex-shrink-0 uppercase"
                        >
                          {((r.user_name || r.user_handle || '?').trim()[0] || '?').toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm">
                          {r.user_handle ? (
                            <span className="text-amber-600 dark:text-amber-400">@{r.user_handle}</span>
                          ) : (
                            <span className="text-zinc-500 italic">{r.user_name || 'unknown'}</span>
                          )}
                        </div>
                        {r.user_name && r.user_handle && (
                          <div className="text-xs text-zinc-500 truncate">{r.user_name}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/prayer/${r.id}`} className="hover:underline" data-testid={`prayer-title-link-${r.id}`}>
                      {r.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 tabular-nums whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 tabular-nums whitespace-nowrap">{fmt(r.updated_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      r.perspective_type === 'first_person'
                        ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                        : 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-400'
                    }`}>
                      {r.perspective_type === 'first_person' ? '1st person' : '3rd person'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`prayer-type-${r.id}`}>
                    {(() => {
                      const meta = TYPE_META[r.type] || TYPE_META.text;
                      return (
                        <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>
                          {meta.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 whitespace-nowrap" data-testid={`prayer-location-${r.id}`}>
                    {(r.creator_lat != null && r.creator_lon != null) ? (
                      <a
                        href={`https://www.google.com/maps?q=${r.creator_lat},${r.creator_lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline text-amber-600 dark:text-amber-400"
                        title={`${Number(r.creator_lat).toFixed(4)}, ${Number(r.creator_lon).toFixed(4)} — open in Google Maps`}
                      >
                        {r.location_area || `${Number(r.creator_lat).toFixed(2)}, ${Number(r.creator_lon).toFixed(2)}`}
                      </a>
                    ) : (
                      <span className="text-zinc-400 dark:text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 tabular-nums whitespace-nowrap" data-testid={`plays-count-${r.id}`}>
                    {Number(r.plays_count) || 0}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(r)}
                      disabled={removingId === r.id}
                      data-testid={`delete-prayer-btn-${r.id}`}
                      title={`Delete "${r.title}"`}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                    >
                      {removingId === r.id
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Trash2 size={16} />}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
        <Link to="/admin/users" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Users</Link>
        <Link to="/admin/reports" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Misuse Reports</Link>
        <Link to="/admin/feedback" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Feedback</Link>
        <Link to="/admin/activity" className="underline hover:text-zinc-800 dark:hover:text-zinc-200">Activity</Link>
      </div>
      {confirmDialog}
    </div>
  );
};

export default AdminPrayers;
